import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { useRef, useState, useEffect } from "react";
import { useHolidaySavingsAmount } from "../hooks/useHolidaySavingsAmount";
import { EmployeeHolidaySavingsRecord } from "../api/salary/holiday-savings.service";
import { formatAmount, formatDate } from "../helper/common.helper";
import CalendarSingle from "./calendar/calendar-single.component";
import "./holiday-pay-calendar.scss";
import LoadingCard from '../components/SkeletonLoading'
export const HolidayInvoiceHistoryModal = () => {
    const { t } = useTranslation();
    const fromCalendarRef = useRef<HTMLDivElement>(null);
    const toCalendarRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [showFromCalendar, setShowFromCalendar] = useState<boolean>(false);
    const [showToCalendar, setShowToCalendar] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: '',
        toDate: '',
        status: ''
    });

    const [toolTipError, setToolTipError] = useState({
        invalidFromdate: false,
        toDateNotSelected: false,
        fromDateNotSelected: false,
    });
    const {
        fetchHolidaySavingsAmount,
        holidaySavingsRecords,
        loading,
        error: hookError,
        currentPage,
        totalPages,
        goToNextPage,
        goToPreviousPage,
    } = useHolidaySavingsAmount(10);


    const handleFromDateSelect = (date: string) => {
        setFromDate(date);
        setShowFromCalendar(false);
        setToolTipError({ ...toolTipError, fromDateNotSelected: false, invalidFromdate: false });
    };

    const handleToDateSelect = (date: string) => {
        setToDate(date);
        setShowToCalendar(false);
        setToolTipError({ ...toolTipError, toDateNotSelected: false, invalidFromdate: false });
    };

    const handleClearFilters = () => {
        setFromDate('');
        setToDate('');
        setStatus('');
        setAppliedFilters({
            fromDate: '',
            toDate: '',
            status: ''
        });
        setToolTipError({
            invalidFromdate: false,
            toDateNotSelected: false,
            fromDateNotSelected: false,
        });
    };

    // Fetch data on component mount and when filters change
    useEffect(() => {
        fetchHolidaySavingsAmount(fromDate, toDate);
    }, [fromDate, toDate, fetchHolidaySavingsAmount]);
    return (
        <div className="invoice-table-container">

            <div className="invoice-table-header">
                <h3 className="invoice-table-title">{t('HOLIDAY_TRANSACTION_HISTORY')}</h3>
                <div className="holiday-filter-section">
                    <div className="holiday-filter-inputs">
                        <div className="holiday-filter-input" ref={fromCalendarRef}>
                            <div className="holiday-filter-input-box" onClick={() => setShowFromCalendar(!showFromCalendar)}>
                                <span className="holiday-filter-label">
                                    {fromDate ? new Date(fromDate).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                    }) : t('From')}
                                </span>
                                {toolTipError.fromDateNotSelected && (
                                    <div className="tooltip-error">
                                        {t('PLEASE_SELECT_FROM_DATE')}
                                    </div>
                                )}
                                {toolTipError.invalidFromdate && (
                                    <div className="tooltip-error">
                                        {t('FROM_DATE_CANNOT_BE_GREATER_THAN_TO_DATE')}
                                    </div>
                                )}
                                <div className="holiday-filter-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                        <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                        <path d="M8 14H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M12 14H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M16 14H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M8 17H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M12 17H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M16 17H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </div>
                            </div>
                            {showFromCalendar && (
                                <div className="calendar-dropdown">
                                    <div className="calendar-dropdown-header">
                                        <span>{t('SELECT_FROM_DATE')}</span>
                                        <button
                                            type="button"
                                            className="calendar-dropdown-close"
                                            onClick={() => setShowFromCalendar(false)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <CalendarSingle
                                        onDateSelect={(date) => {
                                            const formattedDate = date.toISOString().split('T')[0];
                                            handleFromDateSelect(formattedDate);
                                        }}
                                        maxYear={new Date().getFullYear()}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="holiday-filter-input" ref={toCalendarRef}>
                            <div className="holiday-filter-input-box" onClick={() => setShowToCalendar(!showToCalendar)}>
                                <span className="holiday-filter-label">
                                    {toDate ? new Date(toDate).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                    }) : t('To')}
                                </span>
                                {toolTipError.toDateNotSelected && (
                                    <div className="tooltip-error">
                                        {t('PLEASE_SELECT_TO_DATE')}
                                    </div>
                                )}
                                <div className="holiday-filter-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                        <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                        <path d="M8 14H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M12 14H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M16 14H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M8 17H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M12 17H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                        <path d="M16 17H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </div>
                            </div>
                            {showToCalendar && (
                                <div className="calendar-dropdown">
                                    <div className="calendar-dropdown-header">
                                        <span>{t('SELECT_TO_DATE')}</span>
                                        <button
                                            type="button"
                                            className="calendar-dropdown-close"
                                            onClick={() => setShowToCalendar(false)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <CalendarSingle
                                        onDateSelect={(date) => {
                                            const formattedDate = date.toISOString().split('T')[0];
                                            handleToDateSelect(formattedDate);
                                        }}
                                        maxYear={new Date().getFullYear()}
                                    />
                                </div>
                            )}
                        </div>
                        <button className="holiday-filter-clear-btn"
                            onClick={handleClearFilters}
                        >
                            {t('Clear')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="invoice-table-content">
                {loading && (
                                <div style={{display: "flex"}}>
                                <LoadingCard/></div>
                            )}
                {!loading && !error && !hookError && holidaySavingsRecords && holidaySavingsRecords.length >= 0 && (
                    <div className="invoice-table">
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('ID')}</TableColumn>
                                <TableColumn>{t('INVOICE_NO')}</TableColumn>
                                <TableColumn>{t('AMOUNT')}</TableColumn>
                                <TableColumn>{t('CREATED_DATE')}</TableColumn>
                            </TableHeader>
                        )}

                        <TableBody>
                            {holidaySavingsRecords.length === 0 && (
                                <div className="invoice-table">
                                    <div className="invoice-table-content">
                                        {(holidaySavingsRecords.length === 0) && (
                                            <div className='d-flex justify-content-center flex-column align-items-center pt-5'>
                                                <span className='pt-3'>{t('NO_DATA_FOUND')}.</span>
                                                {error}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {isMobile ? (
                                holidaySavingsRecords?.map((record: EmployeeHolidaySavingsRecord) => (
                                    <MobileCardWrapper key={record.invoiceId || Math.random()}>
                                        <MobileCardRow label={t('ID')}>
                                            <span className="invoice-table-detailed__invoice-link">{record.invoiceId || 'N/A'}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('INVOICE_NO')}>
                                            {record.invoiceNumber ?? 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('AMOUNT')}>
                                            {formatAmount(record.holidaySavingsAmount) ?? 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CREATED_DATE')}>
                                            {formatDate(record.createdDate) ?? 'N/A'}
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                holidaySavingsRecords.map((record: EmployeeHolidaySavingsRecord) => (
                                    <TableRow key={record.invoiceId || Math.random()}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{record.invoiceId || 'N/A'}</span>
                                        </TableCell>
                                        <TableCell>{record.invoiceNumber ?? 'N/A'}</TableCell>
                                        <TableCell>{formatAmount(record.holidaySavingsAmount) ?? 'N/A'}</TableCell>
                                        <TableCell>{formatDate(record.createdDate) ?? 'N/A'}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>
                )}


                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', marginTop: '1rem' }}>
                        <button
                            onClick={goToPreviousPage}
                            disabled={currentPage === 1}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: currentPage === 1 ? '#ccc' : '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {t('PREVIOUS')}
                        </button>
                        <span style={{ margin: '0 1rem' }}>
                            {t('PAGE')} {currentPage} {t('OF')} {totalPages}
                        </span>
                        <button
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: currentPage === totalPages ? '#ccc' : '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {t('NEXT')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}